import { useMutation, useQuery } from "@tanstack/react-query";
import { CACHE_TIERS, QUERY_KEYS } from "@workspace/types";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { useRouter } from "next/navigation";
import { useSupabase } from "@/components/providers/supabase-auth-provider";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";
import { trpc } from "@/lib/trpc/react";

// Types
export interface CurrentUserDisplay {
	id: string;
	email: string;
	name: string;
	username: string | null;
	avatar: string | null;
	has_set_username: boolean;
}

/**
 * Consolidated auth hook - combines all auth operations and current user display
 * Replaces: use-auth-mutations, use-current-user-display
 */

// Current user display data - now using tRPC
export function useCurrentUserDisplay() {
	const { user } = useSupabase();

	const { data, isLoading, error } = trpc.user.getCurrentUserDisplay.useQuery(
		undefined,
		{
			...CACHE_TIERS.REALTIME, // Real-time for current user data
			retry: false,
			enabled: !!user, // Only run query if user is authenticated
		},
	);

	return {
		data: data || null,
		isLoading: !!user && isLoading, // Only loading if user exists and query is running
		error,
	};
}

// NOTE: Sign in, sign up, and password reset mutations have been migrated to Server Actions
// These are implemented in the respective form components using useActionState hook
// Keeping only the mutations that are still needed for client-side operations

// Reset Password Mutation
export function useResetPasswordMutation() {
	const router = useRouter();
	const supabase = createClient();

	return useMutation({
		mutationFn: async ({ password }: { password: string }) => {
			// Session should already be established by callback route
			const {
				data: { session },
			} = await supabase.auth.getSession();

			if (!session) {
				throw new Error(
					"No active session. Please request a new password reset link.",
				);
			}

			const { error: updateError } = await supabase.auth.updateUser({
				password: password,
			});

			if (updateError) {
				throw updateError;
			}

			// Refresh the session to ensure auth state is up to date
			const { error: refreshError } = await supabase.auth.refreshSession();
			if (refreshError) {
				console.error(
					"Error refreshing session after password reset:",
					refreshError,
				);
			}
		},
		onSuccess: async () => {
			toast({ description: "Password updated" });

			// First refresh the router to update server components
			router.refresh();

			// Then redirect using window.location for a full page refresh
			// This ensures all components (including TopNav) get the updated auth state
			setTimeout(() => {
				window.location.href = "/festivals";
			}, 2000);
		},
		onError: (error: Error) => {
			toast({
				variant: "destructive",
				description: error.message || "Failed to reset password",
			});
		},
	});
}

// Update Password Mutation
export function useUpdatePasswordMutation() {
	const router = useRouter();
	const supabase = createClient();

	return useMutation({
		mutationFn: async (password: string) => {
			const { error: updateError } = await supabase.auth.updateUser({
				password,
			});

			if (updateError) {
				throw updateError;
			}
		},
		onSuccess: () => {
			toast({ description: "Password updated" });
			router.push("/home");
			router.refresh();
		},
		onError: (error: Error) => {
			toast({
				variant: "destructive",
				description: error.message || "Failed to update password",
			});
		},
	});
}

// Email Verification Mutation
export function useVerifyEmailMutation() {
	const router = useRouter();
	const supabase = createClient();

	return useMutation({
		mutationFn: async ({ email, token }: { email: string; token: string }) => {
			const { error } = await supabase.auth.verifyOtp({
				email,
				token,
				type: "signup",
			});

			if (error) {
				throw error;
			}
		},
		onSuccess: () => {
			// Redirect after a delay
			setTimeout(() => {
				router.push("/festivals");
			}, 3000);
		},
	});
}

// Inviter Info Query (used in auth flows)
export function useInviterInfo(inviteCode: string | null) {
	const supabase = createClient();

	return useQuery({
		queryKey: QUERY_KEYS.user.profile(inviteCode || ""),
		queryFn: async () => {
			if (!inviteCode) return null;

			type ProfileSelect = Pick<Database["public"]["Tables"]["profiles"]["Row"], 
				"id" | "full_name" | "username">;
			
			const { data: profile, error } = await supabase
				.from("profiles")
				.select("id, full_name, username")
				.eq("invite_code", inviteCode)
				.single() as { data: ProfileSelect | null; error: any };

			if (error || !profile) return null;

			const inviterInfo = {
				id: profile.id,
				// Always use username for display - never expose real names
				name: profile.username || "A friend",
				username: profile.username,
			};

			return inviterInfo;
		},
		enabled: !!inviteCode,
		...CACHE_TIERS.REALTIME, // 5 minutes
	});
}
